import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { NavLink } from 'react-router-dom';
import { MAX_BOTTOM_DOCK_PINS } from '../config/crmNavDock';
import '../styles/crm-bottom-dock-launcher-premium.css';

function filterLauncherGroups(groups, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return groups;
  return groups
    .map((group) => {
      const groupMatch = String(group.label || '').toLowerCase().includes(q);
      const children = groupMatch
        ? group.children
        : group.children.filter((it) => String(it.label || '').toLowerCase().includes(q));
      return { ...group, children };
    })
    .filter((group) => group.children.length > 0);
}

function LauncherTile({
  item,
  isPinned,
  isOnlyPinned,
  isActive,
  isDragging,
  onTogglePin,
  onNavigate,
  onDragStart,
  onDragEnd,
  staggerIndex,
  variant = 'grid',
  pinLimitReached = false
}) {
  const isCompact = variant === 'dock';
  const pinDisabled = (isOnlyPinned && isPinned) || (!isPinned && pinLimitReached);
  return (
    <article
      className={`crm-bdl-tile${isCompact ? ' crm-bdl-tile--dock' : ''}${isActive ? ' is-active' : ''}${isPinned ? ' is-pinned' : ''}${isDragging ? ' is-dragging' : ''}`}
      style={{ '--bdl-stagger': `${Math.min(staggerIndex, 11) * 0.03}s` }}
      draggable={Boolean(onDragStart)}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
    >
      <NavLink
        to={item.to}
        className="crm-bdl-tile-link"
        title={item.label}
        onClick={onNavigate}
      >
        <span className="crm-bdl-tile-icon-wrap">
          <span className="crm-bdl-tile-icon" aria-hidden>{item.icon}</span>
          {Number(item.unread || 0) > 0 ? (
            <span className="crm-bdl-tile-badge" aria-label={`${item.unread} pendientes`}>
              {Number(item.unread || 0) > 99 ? '99+' : Number(item.unread || 0)}
            </span>
          ) : null}
          {isPinned && !isCompact ? <span className="crm-bdl-tile-dock-mark" aria-hidden title="En tu barra" /> : null}
        </span>
        <span className="crm-bdl-tile-label">{item.label}</span>
      </NavLink>
      <button
        type="button"
        className={`crm-bdl-tile-pin${isPinned ? ' is-on' : ''}${isCompact ? ' crm-bdl-tile-pin--dock' : ''}`}
        aria-pressed={isPinned}
        disabled={pinDisabled}
        draggable={false}
        aria-label={
          pinLimitReached && !isPinned
            ? `Barra llena (máx. ${MAX_BOTTOM_DOCK_PINS})`
            : (isPinned ? `Quitar ${item.label} de la barra` : `Fijar ${item.label} en la barra`)
        }
        title={
          pinLimitReached && !isPinned
            ? `Máximo ${MAX_BOTTOM_DOCK_PINS} en la barra`
            : (isPinned ? 'Quitar de la barra' : 'Fijar en la barra')
        }
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onTogglePin(item.to);
        }}
      >
        <span className="crm-bdl-tile-pin-icon" aria-hidden>{isPinned ? '−' : '+'}</span>
      </button>
    </article>
  );
}

function LauncherPanel({
  navGroups,
  pinnedPaths,
  pinnedCount,
  totalCount,
  activePath,
  searchQuery,
  onSearchQueryChange,
  onClose,
  onTogglePin,
  onNavigate,
  draggingPath,
  onItemDragStart,
  onItemDragEnd,
  maxPins = MAX_BOTTOM_DOCK_PINS,
  dockLimitFlash = false
}) {
  const [areaFilter, setAreaFilter] = useState('all');
  const query = searchQuery;
  const isSearching = Boolean(String(query || '').trim());

  const filteredGroups = useMemo(
    () => filterLauncherGroups(navGroups, query),
    [navGroups, query]
  );

  const areaFilteredGroups = useMemo(() => {
    if (areaFilter === 'all') return filteredGroups;
    return filteredGroups.filter((g) => g.key === areaFilter);
  }, [filteredGroups, areaFilter]);

  const filteredTotal = useMemo(
    () => areaFilteredGroups.reduce((acc, g) => acc + g.children.length, 0),
    [areaFilteredGroups]
  );

  const pinSet = pinnedPaths || new Set();
  const isOnlyPinned = pinSet.size <= 1;
  const dockPct = maxPins > 0
    ? Math.min(100, Math.round((pinnedCount / maxPins) * 100))
    : (totalCount > 0 ? Math.round((pinnedCount / totalCount) * 100) : 0);
  const pinLimitReached = pinnedCount >= maxPins;
  const dockMeterFull = pinLimitReached;

  const pinnedItems = useMemo(() => {
    const items = [];
    navGroups.forEach((group) => {
      group.children.forEach((item) => {
        if (pinSet.has(item.to)) {
          items.push({ ...item, groupKey: group.key, groupLabel: group.label });
        }
      });
    });
    return items;
  }, [navGroups, pinSet]);

  const showDockStrip = !isSearching && areaFilter === 'all' && pinnedItems.length > 0;

  const hasVisibleTiles = useMemo(() => (
    areaFilteredGroups.some((group) => {
      const children = showDockStrip
        ? group.children.filter((it) => !pinSet.has(it.to))
        : group.children;
      return children.length > 0;
    })
  ), [areaFilteredGroups, showDockStrip, pinSet]);

  let stagger = 0;

  useEffect(() => {
    if (isSearching) setAreaFilter('all');
  }, [isSearching]);

  return (
    <div
      className="crm-bdl-panel"
      role="dialog"
      aria-modal="true"
      aria-labelledby="crm-bdl-title"
    >
      <div className="crm-bdl-panel__glow" aria-hidden="true" />
      <header className="crm-bdl-panel__head">
        <div className="crm-bdl-panel__head-copy">
          <div className="crm-bdl-panel__title-row">
            <span className="crm-bdl-panel__title-mark" aria-hidden>
              <span className="crm-bdl-panel__title-mark-grid">
                <span /><span /><span /><span />
              </span>
            </span>
            <div
              className="crm-bdl-panel__title-block"
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-end',
                gap: '20px',
                width: '100%',
              }}
            >
              <div>
                <p
                  className="crm-bdl-panel__eyebrow"
                  style={{ margin: 0 }}
                >
                  Grupo Bienestar
                </p>

                <h2
                  id="crm-bdl-title"
                  className="crm-bdl-panel__title"
                  style={{ margin: 0 }}
                >
                  <span className="crm-bdl-panel__title-hub">
                    Command Hub
                  </span>
                </h2>
              </div>

              <p
                className="crm-bdl-panel__lede"
                style={{
                  margin: 0,
                  textAlign: 'right',
                  opacity: 0.82,
                  fontSize: '0.85rem',
                  whiteSpace: 'nowrap',
                }}
              >
                Entrá con un toque · Fijá con <strong>+</strong> en la tarjeta
              </p>
            </div>
          </div>
          <div
            className={`crm-bdl-dock-meter${dockMeterFull ? ' is-full' : ''}`}
            aria-label={`${pinnedCount} de ${maxPins} módulos en tu barra`}
          >
            <div className="crm-bdl-dock-meter__track">
              <span className="crm-bdl-dock-meter__fill" style={{ width: `${dockPct}%` }} />
            </div>
            <span className="crm-bdl-dock-meter__label">
              <strong>{pinnedCount}</strong>
              <span className="crm-bdl-dock-meter__muted"> / {maxPins} en barra</span>
            </span>
          </div>
        </div>
        <button
          type="button"
          className="crm-bdl-panel__close"
          onClick={onClose}
          aria-label="Cerrar Command Hub"
        >
          ×
        </button>
      </header>

      {/* {pinLimitReached ? (
        <div
          className={`crm-bdl-limit-banner${dockLimitFlash ? ' is-flash' : ''}`}
          role="alert"
          aria-live="assertive"
        >
          <span className="crm-bdl-limit-banner__icon" aria-hidden>!</span>
          <div className="crm-bdl-limit-banner__copy">
            <strong>Barra completa — {maxPins} de {maxPins}</strong>
            <span>Quitá un acceso de tu barra inferior para agregar otro módulo.</span>
          </div>
        </div>
      ) : null} */}
      {/* 
      <div className="crm-bdl-panel__search-wrap">
        <span className="crm-bdl-panel__search-icon" aria-hidden>⌕</span>
        <input
          type="search"
          className="crm-bdl-panel__search"
          placeholder="Buscar módulo…"
          value={query}
          onChange={(e) => onSearchQueryChange(e.target.value)}
          aria-label="Buscar módulos"
        />
        {query ? (
          <button
            type="button"
            className="crm-bdl-panel__search-clear"
            onClick={() => onSearchQueryChange('')}
            aria-label="Limpiar búsqueda"
          >
            ×
          </button>
        ) : null}
      </div> */}
      {/* 
      {!isSearching ? (
        <div className="crm-bdl-area-filters" role="tablist" aria-label="Filtrar por área">
          <button
            type="button"
            role="tab"
            aria-selected={areaFilter === 'all'}
            className={`crm-bdl-area-chip${areaFilter === 'all' ? ' is-active' : ''}`}
            onClick={() => setAreaFilter('all')}
          >
            Todos
          </button>
          {navGroups.map((group) => (
            <button
              key={`bdl-area-${group.key}`}
              type="button"
              role="tab"
              aria-selected={areaFilter === group.key}
              className={`crm-bdl-area-chip${areaFilter === group.key ? ' is-active' : ''}`}
              onClick={() => setAreaFilter(group.key)}
            >
              <span aria-hidden>{group.icon}</span>
              {group.label}
            </button>
          ))}
        </div>
      ) : null} */}

      {showDockStrip ? (
        <div className="crm-bdl-panel__dock-zone">
          <section className="crm-bdl-dock-strip" aria-label="Módulos en tu barra">
            <header className="crm-bdl-dock-strip__head">
              <span className="crm-bdl-dock-strip__eyebrow">Tu barra inferior</span>
              <span className="crm-bdl-dock-strip__hint">
                {pinLimitReached
                  ? `Barra completa (${maxPins} máx.)`
                  : 'Arrastrá para reordenar en el dock'}
              </span>
            </header>
            <div className="crm-bdl-dock-strip__scroll">
              {pinnedItems.map((item, index) => (
                <LauncherTile
                  key={`dock-${item.to}`}
                  item={item}
                  variant="dock"
                  isPinned
                  isOnlyPinned={isOnlyPinned}
                  isActive={activePath === item.to}
                  isDragging={draggingPath === item.to}
                  onTogglePin={onTogglePin}
                  onNavigate={onNavigate}
                  onDragStart={onItemDragStart ? onItemDragStart(item) : undefined}
                  onDragEnd={onItemDragEnd}
                  staggerIndex={index}
                  pinLimitReached={pinLimitReached}
                />
              ))}
            </div>
          </section>
        </div>
      ) : null}

      <div className="crm-bdl-panel__body">
        {hasVisibleTiles ? (
          areaFilteredGroups.map((group) => {
            const unpinnedChildren = showDockStrip
              ? group.children.filter((it) => !pinSet.has(it.to))
              : group.children;
            if (!unpinnedChildren.length) return null;
            return (
              <section key={`bdl-group-${group.key}`} className="crm-bdl-group">
                <header className="crm-bdl-group__head">
                  <span className="crm-bdl-group__icon" aria-hidden>{group.icon}</span>
                  <h3 className="crm-bdl-group__title">{group.label}</h3>
                </header>
                <div className="crm-bdl-grid">
                  {unpinnedChildren.map((item) => {
                    const tileStagger = stagger;
                    stagger += 1;
                    return (
                      <LauncherTile
                        key={item.to}
                        item={item}
                        isPinned={pinSet.has(item.to)}
                        isOnlyPinned={isOnlyPinned}
                        isActive={activePath === item.to}
                        isDragging={draggingPath === item.to}
                        onTogglePin={onTogglePin}
                        onNavigate={onNavigate}
                        onDragStart={onItemDragStart ? onItemDragStart(item) : undefined}
                        onDragEnd={onItemDragEnd}
                        staggerIndex={tileStagger}
                        pinLimitReached={pinLimitReached}
                      />
                    );
                  })}
                </div>
              </section>
            );
          })
        ) : (
          <div className="crm-bdl-panel__empty" role="status">
            <div className="crm-bdl-panel__empty-icon" aria-hidden>
              <span className="crm-bdl-panel__empty-icon-ring" />
              <span className="crm-bdl-panel__empty-icon-glyph">⌕</span>
            </div>
            <h3 className="crm-bdl-panel__empty-title">Sin coincidencias</h3>
            <p className="crm-bdl-panel__empty-copy">
              Probá otro nombre o volvé a <strong>Todos</strong>.
            </p>
            {query.trim() ? (
              <button
                type="button"
                className="crm-bdl-panel__empty-action"
                onClick={() => onSearchQueryChange('')}
              >
                Limpiar búsqueda
              </button>
            ) : areaFilter !== 'all' ? (
              <button
                type="button"
                className="crm-bdl-panel__empty-action"
                onClick={() => setAreaFilter('all')}
              >
                Ver todos los módulos
              </button>
            ) : null}
          </div>
        )}
      </div>

      <footer className="crm-bdl-panel__foot">
        <span className="crm-bdl-panel__foot-hint">
          {isSearching || areaFilter !== 'all'
            ? `${filteredTotal} resultado${filteredTotal === 1 ? '' : 's'}`
            : `${totalCount} módulos disponibles`}
        </span>
        <span className="crm-bdl-panel__foot-keys">
          <kbd>Esc</kbd> cerrar
        </span>
      </footer>
    </div>
  );
}

/** Botón en la barra del dock inferior (grid estilo macOS). */
export function CrmBottomDockLauncherButton({ isOpen, onClick }) {
  return (
    <button
      type="button"
      className={`crm-nav-dock-flat-item crm-nav-dock-launcher-btn${isOpen ? ' is-open' : ''}`}
      onClick={onClick}
      aria-expanded={isOpen}
      aria-haspopup="dialog"
      aria-label={isOpen ? 'Cerrar Command Hub' : 'Abrir Command Hub'}
      title={isOpen ? 'Cerrar Command Hub' : 'Grupo Bienestar · Command Hub'}
    >
      <span className="crm-nav-icon crm-bdl-launcher-btn__icon" aria-hidden>
        <span className="crm-bdl-launcher-btn__grid">
          <span /><span /><span /><span />
        </span>
      </span>
      <span className="crm-nav-item-label">Hub</span>
    </button>
  );
}

/** Panel flotante anclado al dock inferior (portal). */
export function CrmBottomDockLauncherPortal({
  isOpen,
  navGroups,
  pinnedPaths,
  pinnedCount,
  totalCount,
  activePath,
  searchQuery,
  onSearchQueryChange,
  onClose,
  onTogglePin,
  draggingPath,
  onItemDragStart,
  onItemDragEnd,
  maxPins = MAX_BOTTOM_DOCK_PINS,
  dockLimitFlash = false
}) {
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen || typeof document === 'undefined') return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  if (typeof document === 'undefined' || !isOpen) return null;

  return createPortal(
    <div className="crm-bdl-shell is-open" aria-hidden={false}>
      <button
        type="button"
        className="crm-bdl-shell__backdrop"
        onClick={onClose}
        aria-label="Cerrar Command Hub"
      />
      <LauncherPanel
        navGroups={navGroups}
        pinnedPaths={pinnedPaths}
        pinnedCount={pinnedCount}
        totalCount={totalCount}
        activePath={activePath}
        searchQuery={searchQuery}
        onSearchQueryChange={onSearchQueryChange}
        onClose={onClose}
        onTogglePin={onTogglePin}
        onNavigate={onClose}
        draggingPath={draggingPath}
        onItemDragStart={onItemDragStart}
        onItemDragEnd={onItemDragEnd}
        maxPins={maxPins}
        dockLimitFlash={dockLimitFlash}
      />
    </div>,
    document.body
  );
}
